import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Avatar,
  CircularProgress,
  LinearProgress,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  Link as LinkIcon,
  AttachMoney,
  Warning,
  CheckCircle,
  Error,
  Schedule,
  Add,
  PersonAdd,
  Assignment,
  Notifications,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { dashboardService, DashboardRange } from '../services/dashboardService';
import { publisherService } from '../services/publisherService';
import { projectService } from '../services/projectService';
import { useProjectWorkflowOptional } from '../contexts/ProjectWorkflowContext';
import { Project } from '../types';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Utility functions
const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${month} ${day}, ${hours}:${minutes} ${ampm}`;
};

const getStageColor = (stage: string) => {
  switch (stage.toLowerCase()) {
    case 'brief_pending': return 'warning';
    case 'content_creation': return 'info';
    case 'content_review': return 'secondary';
    case 'published': return 'success';
    case 'completed': return 'success';
    default: return 'default';
  }
};

const DashboardPage: React.FC = () => {
  const { isSuperAdmin, isAdmin, isPublisher, isClient, isAgency, user } = useAuth();
  const navigate = useNavigate();
  const workflow = useProjectWorkflowOptional();
  
  const [statsRange, setStatsRange] = useState<DashboardRange>('all');

  const { data: workflowProjectsData } = useQuery({
    queryKey: ['dashboard-workflow-first-project', user?.clientId, isClient, isAgency],
    queryFn: () =>
      projectService.getProjects({
        ...(isClient && user?.clientId ? { clientId: user.clientId, limit: 500 } : { limit: 1 }),
      }),
    enabled: (isClient && Boolean(user?.clientId)) || isAgency,
  });

  const workflowFirstProject = useMemo((): Project | null => {
    if (workflow?.selectedProject && (isClient || isAgency)) {
      const sp = workflow.selectedProject;
      return {
        id: sp.id,
        projectName: sp.projectName,
        clientId: sp.clientId,
        client: { id: sp.clientId, name: sp.clientName },
      } as Project;
    }
    const projects = workflowProjectsData?.data || [];
    if (!projects.length) return null;
    return projects[0];
  }, [workflowProjectsData, workflow?.selectedProject, isClient, isAgency]);

  const handleTopCategoryClick = useCallback(
    (category: string) => {
      if (isClient || isAgency) {
        const project = workflowFirstProject;
        if (!project) return;
        const clientId = project.clientId || project.client?.id || '';
        const clientName =
          project.client?.name ||
          workflow?.selectedClient?.name ||
          (isClient ? 'My account' : 'Client');
        if (workflow) {
          workflow.selectProject(project, clientName, 0, 'sites');
        }
        navigate(
          `/sites?projectId=${encodeURIComponent(project.id)}&clientId=${encodeURIComponent(clientId)}`,
          { state: { categoryFilter: category }, replace: Boolean(workflow) }
        );
        return;
      }
      navigate('/sites', { state: { categoryFilter: category } });
    },
    [isClient, isAgency, workflowFirstProject, workflow, navigate]
  );

  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['dashboard-metrics', statsRange],
    queryFn: () => dashboardService.getDashboardMetrics(statsRange),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    enabled: !isPublisher, // Don't load admin metrics for publishers
    placeholderData: (prev) => prev, // keep previous data while switching range
  });

  // Publisher earnings query
  const { data: publisherEarnings, isLoading: earningsLoading } = useQuery({
    queryKey: ['publisher-earnings'],
    queryFn: publisherService.getPublisherEarnings,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    enabled: isPublisher,
  });

  // All publisher earnings query (for Super Admin)
  const { data: allPublisherEarnings, isLoading: allEarningsLoading } = useQuery({
    queryKey: ['all-publisher-earnings'],
    queryFn: publisherService.getAllPublisherEarnings,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    enabled: isSuperAdmin,
  });

  if (isLoading || earningsLoading || allEarningsLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  if (error) {
    return (
      <Box className="error-container">
        <Alert severity="error">
          Failed to load dashboard data. Please try again.
        </Alert>
      </Box>
    );
  }

  const getStageColor = (stage: string) => {
    switch (stage.toLowerCase()) {
      case 'brief_pending': return 'warning';
      case 'content_creation': return 'info';
      case 'content_review': return 'secondary';
      case 'published': return 'success';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${month} ${day}, ${hours}:${minutes} ${ampm}`;
  };

  // Publisher Dashboard
  if (isPublisher) {
    return (
      <main style={{
        padding: '24px',
        background: 'var(--bg)',
        minHeight: '100vh'
      }}>
        {/* Dashboard Header */}
        <header style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              margin: '0 0 4px 0',
              lineHeight: 1.2
            }}>
              {getGreeting()}, {user?.firstName} 😊
            </h1>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              margin: 0
            }}>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })} — Track your earnings and placements.
            </p>
          </div>
        </header>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Total Earnings Card */}
          <div className="publisher-stat-card" style={{
            background: 'var(--surface)',
            borderRadius: 'var(--radius)',
            padding: '20px',
            cursor: 'pointer',
            boxShadow: 'var(--shadow)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'var(--green-soft)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AttachMoney style={{ 
                  color: 'var(--green)', 
                  fontSize: '18px' 
                }} />
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                lineHeight: 1,
                marginBottom: '4px'
              }}>
                {formatCurrency(publisherEarnings?.totalEarnings || 0)}
              </div>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                fontWeight: 500
              }}>
                Total Earnings
              </div>
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-muted)',
              marginTop: '8px'
            }}>
              Current balance from completed tasks
            </div>
          </div>

          {/* Completed Placements Card */}
          <div className="publisher-stat-card" style={{
            background: 'var(--surface)',
            borderRadius: 'var(--radius)',
            padding: '20px',
            cursor: 'pointer',
            boxShadow: 'var(--shadow)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'var(--accent-soft)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle style={{ 
                  color: 'var(--accent)', 
                  fontSize: '18px' 
                }} />
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                lineHeight: 1,
                marginBottom: '4px'
              }}>
                {publisherEarnings?.completedPlacements || 0}
              </div>
              <div style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                fontWeight: 500
              }}>
                Completed Placements
              </div>
            </div>
            <div style={{
              height: '6px',
              background: 'var(--surface2)',
              borderRadius: '3px',
              overflow: 'hidden',
              marginBottom: '6px'
            }}>
              <div style={{
                height: '100%',
                width: `${publisherEarnings?.totalPlacements ? Math.round((publisherEarnings.completedPlacements / publisherEarnings.totalPlacements) * 100) : 0}%`,
                background: 'linear-gradient(90deg, var(--accent) 0%, #1d4ed8 100%)',
                borderRadius: '3px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>{publisherEarnings?.completedPlacements || 0} of {publisherEarnings?.totalPlacements || 0} completed</span>
              <span>{publisherEarnings?.totalPlacements ? Math.round((publisherEarnings.completedPlacements / publisherEarnings.totalPlacements) * 100) : 0}%</span>
            </div>
          </div>
        </div>

        {/* Welcome Message Card */}
        <div style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius)',
          padding: '20px',
          boxShadow: 'var(--shadow)',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'var(--accent-soft)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Notifications style={{ 
                color: 'var(--accent)', 
                fontSize: '18px' 
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                margin: '0 0 6px 0'
              }}>
                Welcome back, {user?.firstName}!
              </h3>
              <p style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                margin: 0,
                lineHeight: 1.5
              }}>
                View and manage your placements from the Placements section. Update placement status as you complete tasks to track your earnings.
              </p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={{
      padding: '24px',
      background: 'var(--bg)',
      minHeight: '100vh'
    }}>
      {/* Dashboard Header */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 600,
            color: '#1a1a1a',
            margin: '0 0 4px 0',
            lineHeight: 1.2
          }}>
            {getGreeting()}, {user?.firstName || 'User'} 😊
          </h1>
          <p style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
            margin: 0
          }}>
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })} — Here's what's happening today.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>Stats:</span>
          <select
            value={statsRange}
            onChange={(e) => setStatsRange(e.target.value as DashboardRange)}
            style={{
              appearance: 'none',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '8px 32px 8px 12px',
              fontFamily: 'inherit',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text-primary)',
              background: "var(--surface) url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\") no-repeat right 12px center",
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All time</option>
            <option value="7d">Last 7 days</option>
            <option value="month">This month</option>
            <option value="lastMonth">Last month</option>
            <option value="year">This year</option>
          </select>
        </div>
      </header>
      
      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Total Placements Stat Card */}
        <div className="stat-card-animation" style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius)',
          padding: '20px',
          cursor: 'pointer',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'var(--accent-soft)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ShoppingCart style={{ 
                color: 'var(--accent)', 
                fontSize: '18px' 
              }} />
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: (metrics?.weeklyTrends?.placements ?? 0) >= 0 ? 'var(--green-soft)' : 'var(--red-soft)',
              borderRadius: '99px',
              fontSize: '11px',
              fontWeight: 500,
              color: (metrics?.weeklyTrends?.placements ?? 0) >= 0 ? 'var(--green)' : 'var(--red)'
            }}>
              <span style={{ fontSize: '10px' }}>
                {(metrics?.weeklyTrends?.placements ?? 0) >= 0 ? '↑' : '↓'}
              </span>
              {Math.abs(metrics?.weeklyTrends?.placements ?? 0)}%
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1,
              marginBottom: '4px'
            }}>
              {metrics?.totalPlacements || 0}
            </div>
            <div style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              fontWeight: 500
            }}>
              Total Placements
            </div>
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-muted)',
            marginTop: '8px'
          }}>
            Weekly trend: {(metrics?.weeklyTrends?.placements ?? 0) >= 0 ? 'up' : 'down'} from last week
          </div>
        </div>
        
         {/* Ready to Publish Stat Card */}
        <div className="stat-card-animation" style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius)',
          padding: '20px',
          cursor: 'pointer',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'var(--amber-soft)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle style={{ 
                color: 'var(--amber)', 
                fontSize: '18px' 
              }} />
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: (metrics?.weeklyTrends?.readyToPublish ?? 0) >= 0 ? 'var(--green-soft)' : 'var(--red-soft)',
              borderRadius: '99px',
              fontSize: '11px',
              fontWeight: 500,
              color: (metrics?.weeklyTrends?.readyToPublish ?? 0) >= 0 ? 'var(--green)' : 'var(--red)'
            }}>
              <span style={{ fontSize: '10px' }}>
                {(metrics?.weeklyTrends?.readyToPublish ?? 0) >= 0 ? '↑' : '↓'}
              </span>
              {Math.abs(metrics?.weeklyTrends?.readyToPublish ?? 0)}%
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1,
              marginBottom: '4px'
            }}>
              {metrics?.readyToPublishPlacements || 0}
            </div>
            <div style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              fontWeight: 500
            }}>
              Ready to Publish
            </div>
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-muted)',
            marginTop: '8px'
          }}>
            Weekly trend: {(metrics?.weeklyTrends?.readyToPublish ?? 0) >= 0 ? 'up' : 'down'} from last week
          </div>
        </div>

        {/* Completed Placements Stat Card */}
        <div className="stat-card-animation" style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius)',
          padding: '20px',
          cursor: 'pointer',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'var(--green-soft)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle style={{ 
                color: 'var(--green)', 
                fontSize: '18px' 
              }} />
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: (metrics?.weeklyTrends?.completedPlacements ?? 0) >= 0 ? 'var(--green-soft)' : 'var(--red-soft)',
              borderRadius: '99px',
              fontSize: '11px',
              fontWeight: 500,
              color: (metrics?.weeklyTrends?.completedPlacements ?? 0) >= 0 ? 'var(--green)' : 'var(--red)'
            }}>
              <span style={{ fontSize: '10px' }}>
                {(metrics?.weeklyTrends?.completedPlacements ?? 0) >= 0 ? '↑' : '↓'}
              </span>
              {Math.abs(metrics?.weeklyTrends?.completedPlacements ?? 0)}%
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1,
              marginBottom: '4px'
            }}>
              {metrics?.completedPlacements || 0}
            </div>
            <div style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              fontWeight: 500
            }}>
              Completed Placements
            </div>
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-muted)',
            marginTop: '8px'
          }}>
            Weekly trend: {(metrics?.weeklyTrends?.completedPlacements ?? 0) >= 0 ? 'up' : 'down'} from last week
          </div>
        </div>

        
        {/* Live Link Monitoring Stat Card */}
        <div className="stat-card-animation" style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius)',
          padding: '20px',
          cursor: 'pointer',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'var(--purple-soft)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <LinkIcon style={{ 
                color: 'var(--purple)', 
                fontSize: '18px' 
              }} />
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: (metrics?.liveLinkMonitoringSummary?.liveLinks200Percentage ?? 0) >= 80 ? 'var(--green-soft)' : 'var(--amber-soft)',
              borderRadius: '99px',
              fontSize: '11px',
              fontWeight: 500,
              color: (metrics?.liveLinkMonitoringSummary?.liveLinks200Percentage ?? 0) >= 80 ? 'var(--green)' : 'var(--amber)'
            }}>
              <span style={{ fontSize: '10px' }}>
                {(metrics?.liveLinkMonitoringSummary?.liveLinks200Percentage ?? 0) >= 80 ? '✓' : '→'}
              </span>
              {metrics?.liveLinkMonitoringSummary?.liveLinks200Percentage ?? 0}%
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1,
              marginBottom: '4px'
            }}>
              {metrics?.liveLinkMonitoringSummary?.liveLinks200Count ?? 0}
            </div>
            <div style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              fontWeight: 500
            }}>
              Live Link Monitoring
            </div>
          </div>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-muted)',
            marginBottom: '6px',
            letterSpacing: '0.5px'
          }}>
            {metrics?.liveLinkMonitoringSummary?.liveLinks200Count ?? 0} of {metrics?.liveLinkMonitoringSummary?.total ?? 0} with 200 status
          </div>
          <div style={{
            height: '6px',
            background: 'var(--surface2)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${metrics?.liveLinkMonitoringSummary?.liveLinks200Percentage ?? 0}%`,
              background: (metrics?.liveLinkMonitoringSummary?.liveLinks200Percentage ?? 0) >= 80 
                ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' 
                : 'linear-gradient(90deg, var(--purple) 0%, #6d28d9 100%)',
              borderRadius: '3px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '20px'
      }}>
        {/* Left Column - Charts and Activity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Orders by Stage Chart */}
          <div className="card-animation" style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '24px',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <div>
              <h2 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                margin: '0 0 4px 0'
              }}>
                Placements by Status
              </h2>
              <p style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                margin: 0
              }}>
                Current placement distribution
              </p>
            </div>
            <button 
              onClick={() => navigate('/guest-blog-placements')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: 0
              }}
            >
              View all
            </button>
          </div>
          
          {/* Chart Container */}
          <div style={{ marginTop: '20px' }}>
            {(() => {
              // Calculate total placements for percentage
              const totalPlacements = metrics?.placementsByStatus?.reduce((sum, s) => sum + s.count, 0) || 0;
              
              // Define status display configuration
              // Clients only see the simplified placement statuses.
              const statusConfig = isClient
                ? [
                    { key: 'READY_TO_PUBLISH', label: 'Ready to Publish', color: 'var(--accent)' },
                    { key: 'COMPLETED', label: 'Completed', color: 'var(--green)' },
                    { key: 'FOR_REVIEW', label: 'For Review', color: 'var(--amber)' },
                    { key: 'NEED_CORRECTION', label: 'Need Correction', color: 'var(--red)' },
                  ]
                : [
                    { key: 'READY_TO_PUBLISH', label: 'Ready to Publish', color: 'var(--accent)' },
                    { key: 'FOR_REVIEW', label: 'For Review', color: 'var(--amber)' },
                    { key: 'NEED_CORRECTION', label: 'Need Correction', color: 'var(--red)' },
                    { key: 'COMPLETED', label: 'Completed', color: 'var(--green)' },
                    { key: 'MAIL_SENT', label: 'Mail Sent', color: 'var(--purple)' },
                    { key: 'SITE_REPLACED', label: 'Site Replaced', color: 'var(--text-muted)' },
                    { key: 'HOLD', label: 'Hold', color: '#94A3B8' },
                  ];

              return statusConfig.map((config, index) => {
                const statusData = metrics?.placementsByStatus?.find(s => s.status === config.key);
                const count = statusData?.count || 0;
                const percentage = totalPlacements > 0 ? Math.round((count / totalPlacements) * 100) : 0;

                return (
                  <div key={config.key} style={{ marginBottom: index < statusConfig.length - 1 ? '20px' : '0' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontSize: '14px',
                        color: 'var(--text-primary)',
                        fontWeight: 500
                      }}>
                        {config.label}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        color: 'var(--text-primary)',
                        fontWeight: 600
                      }}>
                        {count}
                      </span>
                    </div>
                    <div style={{
                      height: '8px',
                      background: 'var(--surface2)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${percentage}%`,
                        background: config.color,
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          
          {/* Chart Legend */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border-light)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--accent)'
              }} />
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Ready to Publish</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--amber)'
              }} />
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>For Review</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--red)'
              }} />
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Need Correction</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--green)'
              }} />
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Completed</span>
            </div>
            {!isClient && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--purple)'
                  }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Mail Sent</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--text-muted)'
                  }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Site Replaced</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#94A3B8'
                  }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Hold</span>
                </div>
              </>
            )}
          </div>
          </div>

          {/* Recent Activity Section */}
          <div className="card-animation" style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '24px',
            boxShadow: 'var(--shadow)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  margin: '0 0 4px 0'
                }}>
                  Recent Activity
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  margin: 0
                }}>
                  Last 5 recent activities
                </p>
              </div>
              <button 
                onClick={() => navigate('/audit')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                View logs
              </button>
            </div>
            
            {/* Activity Table */}
            <div style={{ marginTop: '16px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: '12px',
                padding: '12px 0',
                borderBottom: '1px solid var(--border-light)',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase'
              }}>
                <div>Activity</div>
                <div>Type</div>
                <div>Time</div>
              </div>
              {(metrics?.recentActivity || []).slice(0, 5).map((activity: any, index: number) => (
                <div key={activity.id || index} style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  gap: '12px',
                  padding: '12px 0',
                  borderBottom: index === 4 ? 'none' : '1px solid var(--border-light)',
                  fontSize: '14px'
                }}>
                  <div style={{
                    color: 'var(--text-primary)',
                    fontWeight: 500
                  }}>
                    {activity.description || activity.title || activity.action}
                  </div>
                  <div>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '99px',
                      fontSize: '11px',
                      fontWeight: 500,
                      background: activity.entity === 'ORDER' ? 'var(--accent-soft)' : 
                                  activity.entity === 'PLACEMENT' ? 'var(--green-soft)' : 
                                  activity.entity === 'CLIENT' ? 'var(--purple-soft)' : 'var(--amber-soft)',
                      color: activity.entity === 'ORDER' ? 'var(--accent)' : 
                              activity.entity === 'PLACEMENT' ? 'var(--green)' : 
                              activity.entity === 'CLIENT' ? 'var(--purple)' : 'var(--amber)'
                    }}>
                      {activity.entity || activity.type || 'Activity'}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)'
                  }}>
                    {activity.createdAt || activity.timestamp ? (() => {
                      const date = new Date(activity.createdAt || activity.timestamp);
                      const now = new Date();
                      const diffMs = now.getTime() - date.getTime();
                      const diffSecs = Math.floor(diffMs / 1000);
                      const diffMins = Math.floor(diffSecs / 60);
                      const diffHours = Math.floor(diffMins / 60);
                      const diffDays = Math.floor(diffHours / 24);
                      const diffWeeks = Math.floor(diffDays / 7);
                      const diffMonths = Math.floor(diffDays / 30);
                      const diffYears = Math.floor(diffDays / 365);

                      if (diffSecs < 60) return 'Just now';
                      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`;
                      if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
                      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
                      if (diffWeeks < 4) return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
                      if (diffMonths < 12) return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
                      return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
                    })() : 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right Column - Sidebar Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Site Link Health — admin / super admin */}
          {isAdmin && !isClient && !isAgency && !isPublisher && (
          <div className="card-animation" style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '24px',
            boxShadow: 'var(--shadow)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  margin: '0 0 4px 0'
                }}>
                  Site Link Health
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  margin: 0
                }}>
                  Real time status summary
                </p>
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginTop: '16px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 8px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="40" cy="40" r="34" stroke="var(--surface2)" strokeWidth="5" fill="none" />
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      stroke="var(--green)"
                      strokeWidth="5"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 34 * ((metrics?.linkHealthSummary?.live || 0) / Math.max(metrics?.linkHealthSummary?.total || 1, 1))} ${2 * Math.PI * 34}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dasharray 0.5s ease' }}
                    />
                  </svg>
                  <div style={{
                    position: 'absolute',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: 'var(--text-primary)'
                  }}>
                    {metrics?.linkHealthSummary?.live || 0}
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Live Links
                </div>
                <div style={{ fontSize: '11px', color: 'var(--green)', fontWeight: 500, marginTop: '4px' }}>
                  ✓ No errors
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {metrics?.linkHealthSummary?.live || 0} out of {metrics?.linkHealthSummary?.total || 0} sites
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 8px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="40" cy="40" r="34" stroke="var(--surface2)" strokeWidth="5" fill="none" />
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      stroke="var(--red)"
                      strokeWidth="5"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 34 * ((metrics?.linkHealthSummary?.removed || 0) / Math.max(metrics?.linkHealthSummary?.total || 1, 1))} ${2 * Math.PI * 34}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dasharray 0.5s ease' }}
                    />
                  </svg>
                  <div style={{
                    position: 'absolute',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: 'var(--text-primary)'
                  }}>
                    {metrics?.linkHealthSummary?.removed || 0}
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Removed
                </div>
                <div style={{ fontSize: '11px', color: 'var(--red)', fontWeight: 500, marginTop: '4px' }}>
                  ✕ With errors
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {metrics?.linkHealthSummary?.removed || 0} out of {metrics?.linkHealthSummary?.total || 0} sites
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Live Link Monitoring — client, agency, publisher */}
          {(isClient || isAgency || isPublisher) && (
          <div className="card-animation" style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '24px',
            boxShadow: 'var(--shadow)',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/live-link-status')}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/live-link-status'); }}
          role="button"
          tabIndex={0}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  margin: '0 0 4px 0'
                }}>
                  Live Link Monitoring
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  margin: 0
                }}>
                  Order live URL status summary
                </p>
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginTop: '16px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 8px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="40" cy="40" r="34" stroke="var(--surface2)" strokeWidth="5" fill="none" />
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      stroke="var(--green)"
                      strokeWidth="5"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 34 * ((metrics?.liveLinkMonitoringSummary?.ok || 0) / Math.max(metrics?.liveLinkMonitoringSummary?.total || 1, 1))} ${2 * Math.PI * 34}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dasharray 0.5s ease' }}
                    />
                  </svg>
                  <div style={{
                    position: 'absolute',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: 'var(--text-primary)'
                  }}>
                    {metrics?.liveLinkMonitoringSummary?.ok || 0}
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  OK (200)
                </div>
                <div style={{ fontSize: '11px', color: 'var(--green)', fontWeight: 500, marginTop: '4px' }}>
                  ✓ Live links
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {metrics?.liveLinkMonitoringSummary?.ok || 0} of {metrics?.liveLinkMonitoringSummary?.total || 0} links
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 8px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="40" cy="40" r="34" stroke="var(--surface2)" strokeWidth="5" fill="none" />
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      stroke="var(--red)"
                      strokeWidth="5"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 34 * ((metrics?.liveLinkMonitoringSummary?.errorTotal || 0) / Math.max(metrics?.liveLinkMonitoringSummary?.total || 1, 1))} ${2 * Math.PI * 34}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dasharray 0.5s ease' }}
                    />
                  </svg>
                  <div style={{
                    position: 'absolute',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: 'var(--text-primary)'
                  }}>
                    {metrics?.liveLinkMonitoringSummary?.errorTotal || 0}
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Errors
                </div>
                <div style={{ fontSize: '11px', color: 'var(--red)', fontWeight: 500, marginTop: '4px' }}>
                  ✕ Needs attention
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {(metrics?.liveLinkMonitoringSummary?.errors || 0)} errors · {(metrics?.liveLinkMonitoringSummary?.timeout || 0)} timeout
                  {(metrics?.liveLinkMonitoringSummary?.pending || 0) > 0
                    ? ` · ${metrics?.liveLinkMonitoringSummary?.pending} pending`
                    : ''}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Top Categories */}
          <div className="card-animation" style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '24px',
            boxShadow: 'var(--shadow)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  margin: '0 0 4px 0'
                }}>
                  Top Categories
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  margin: 0
                }}>
                  Most popular site categories
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {metrics?.topCategories && metrics.topCategories.length > 0 ? (
                metrics.topCategories.map((cat, index) => {
                  // Format category name: GENERAL → General, AI_TOOL → AI Tool
                  const formatCategoryName = (category: string) => {
                    return category
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ');
                  };

                  // Get first letter of category for icon
                  const getFirstLetter = (category: string) => {
                    return category.charAt(0).toUpperCase();
                  };

                  const formattedName = formatCategoryName(cat.category);

                  return (
                    <div 
                      key={cat.category} 
                      onClick={() => handleTopCategoryClick(cat.category)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: 'var(--radius)',
                        background: 'var(--surface2)',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--surface3)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--surface2)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 
                          index === 0 ? '#2563EB' : // Blue
                          index === 1 ? '#9333EA' : // Purple
                          index === 2 ? '#16A34A' : // Green
                          index === 3 ? '#94A3B8' : // Gray
                          index === 4 ? '#F59E0B' : // Amber
                          index === 5 ? '#EC4899' : // Pink
                          '#64748B', // Slate
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: 'white',
                        flexShrink: 0
                      }}>
                        {getFirstLetter(cat.category)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: 'var(--text-primary)',
                          marginBottom: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {formattedName}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: 'var(--text-muted)'
                        }}>
                          Click to view sites
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexShrink: 0
                      }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 700,
                          color: 'var(--accent)',
                          minWidth: '30px',
                          textAlign: 'right'
                        }}>
                          {cat.count}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: 'var(--text-secondary)'
                        }}>
                          ▶
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: '14px'
                }}>
                  No categories available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default DashboardPage;
